'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { Eye, EyeOff, Shield, ArrowRight, Copy, Check, Zap, FileText, Clock, Mail, Lock, User, ArrowLeft } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  gdprConsent: boolean;
  termsAccepted: boolean;
}

interface ForgotPasswordFormData {
  email: string;
}

const demoCredentials = [
  { id: 'cred-admin', role: 'Admin', email: 'admin@disputely.co.uk', password: 'Admin@2026!', href: '/admin-dashboard' },
  { id: 'cred-user', role: 'User', email: 'sarah.h@disputely.co.uk', password: 'User@2026!', href: '/user-dashboard' },
];

const trustSignals = [
  { id: 'trust-1', icon: Shield, text: 'GDPR-compliant & fully encrypted' },
];

const features = [
  { id: 'feat-1', icon: Zap, label: 'AI Case Builder', desc: 'Legal arguments in minutes' },
  { id: 'feat-2', icon: FileText, label: 'Auto Documents', desc: 'Court-ready letters & appeals' },
  { id: 'feat-3', icon: Clock, label: 'Deadline Tracker', desc: 'Never miss a legal window' },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} className="p-1 hover:bg-slate-100 rounded transition-colors" title="Copy">
      {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} className="text-slate-400" />}
    </button>
  );
}

export default function AuthForm() {
  const [view, setView] = useState<'login' | 'signup' | 'forgot'>('login');
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { signIn, signUp, resetPassword, signInWithGoogle } = useAuth();
  const router = useRouter();

  // Detect Supabase recovery token in the URL hash fragment and redirect to
  // the reset-password page so the user can set a new password.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery') && hash.includes('access_token=')) {
      // Pass the full hash to the reset-password page so Supabase client can
      // pick up the access_token and establish the PASSWORD_RECOVERY session.
      router.replace('/auth/reset-password' + hash);
    }
  }, []);

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', gdprConsent: false, termsAccepted: false },
  });

  const forgotForm = useForm<ForgotPasswordFormData>({
    defaultValues: { email: '' },
  });

  const handleLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Signed in successfully. Welcome back!');
      if (data.email === 'admin@disputely.co.uk') {
        router.push('/admin-dashboard');
      } else {
        router.push('/user-dashboard');
      }
    } catch (error: any) {
      loginForm.setError('email', { message: error?.message || 'Invalid email or password' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName });
      toast.success('Account created! Redirecting to your dashboard...');
      router.push('/user-dashboard');
    } catch (error: any) {
      signupForm.setError('email', { message: error?.message || 'Could not create account. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      await resetPassword(data.email);
      setResetEmailSent(true);
    } catch (error: any) {
      forgotForm.setError('email', { message: error?.message || 'Could not send reset email. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error: any) {
      toast.error(error?.message || 'Could not sign in with Google. Please try again.');
    }
  };

  const autofillCredential = (cred: typeof demoCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    if (mode !== 'login') setMode('login');
    toast.info(`${cred.role} credentials filled in`);
  };

  const switchMode = (newMode: 'login' | 'signup' | 'forgot') => {
    setMode(newMode);
    setResetEmailSent(false);
  };

  return (
    <div className="min-h-screen flex">
      <Toaster position="bottom-right" richColors />

      {/* Brand Panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] bg-gradient-to-br from-blue-700 via-blue-800 to-slate-900 flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-sky-400 rounded-full translate-x-1/4 translate-y-1/4" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-300 rounded-full -translate-x-1/2 -translate-y-1/2" />
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <AppLogo size={40} />
            <span className="text-white font-bold text-xl tracking-tight">Disputely</span>
          </div>
        </div>

        {/* Hero Copy */}
        <div className="relative z-10 space-y-6">
          <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight">
            Fight unfair charges<br />
            <span className="text-sky-300">with AI on your side</span>
          </h1>
          <p className="text-base text-white/70 leading-relaxed max-w-sm">
            Generate legally sound appeal letters, track deadlines, and build court-ready evidence packs — all tailored to UK law.
          </p>

          {/* Features */}
          <div className="space-y-3 pt-2">
            {features.map((f) => {
              const FeatIcon = f.icon;
              return (
                <div key={f.id} className="flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                    <FeatIcon size={16} className="text-sky-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{f.label}</p>
                    <p className="text-xs text-white/60">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trust Signals */}
        <div className="relative z-10 space-y-2.5">
          {trustSignals.map((t) => {
            const TrustIcon = t.icon;
            return (
              <div key={t.id} className="flex items-center gap-2.5">
                <TrustIcon size={14} className="text-sky-300 shrink-0" />
                <span className="text-xs text-white/70">{t.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Form Panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 bg-white overflow-y-auto scrollbar-thin">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2.5 mb-8">
          <AppLogo size={36} />
          <span className="font-bold text-slate-900 text-lg">Disputely</span>
        </div>

        <div className="w-full max-w-md">
          {/* Mode Toggle — only show for login/signup */}
          {mode !== 'forgot' && (
            <div className="flex bg-slate-100 rounded-xl p-1 mb-8">
              {(['login', 'signup'] as const).map((m) => (
                <button
                  key={`mode-${m}`}
                  onClick={() => switchMode(m)}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {m === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <div className="fade-in">
              <h2 className="text-2xl font-bold text-slate-900 mb-1">Welcome back</h2>
              <p className="text-sm text-slate-500 mb-7">Sign in to manage your disputes</p>

              {/* Google OAuth */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 mb-5"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">or sign in with email</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      {...loginForm.register('email', {
                        required: 'Email is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                      })}
                      className={`input-field pl-10 ${loginForm.formState.errors.email ? 'border-red-300 focus:ring-red-400' : ''}`}
                      placeholder="you@email.co.uk"
                    />
                  </div>
                  {loginForm.formState.errors.email && (
                    <p className="mt-1.5 text-xs text-red-600">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium text-slate-700">Password</label>
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...loginForm.register('password', { required: 'Password is required' })}
                      className={`input-field pl-10 pr-10 ${loginForm.formState.errors.password ? 'border-red-300 focus:ring-red-400' : ''}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="mt-1.5 text-xs text-red-600">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div className="flex items-center gap-2.5">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    {...loginForm.register('rememberMe')}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="rememberMe" className="text-sm text-slate-600">Remember me for 30 days</label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3 text-base mt-2"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    <>Sign In <ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              <p className="mt-5 text-center text-sm text-slate-500">
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Create one for free
                </button>
              </p>
            </div>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <div className="fade-in">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
              >
                <ArrowLeft size={15} />
                Back to sign in
              </button>

              {resetEmailSent ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <Mail size={24} className="text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Check your inbox</h2>
                  <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                    We&apos;ve sent a password reset link to your email address. The link will expire in 1 hour.
                  </p>
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="btn-primary w-full py-3 text-base"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-slate-900 mb-1">Reset your password</h2>
                  <p className="text-sm text-slate-500 mb-7">
                    Enter your email address and we&apos;ll send you a link to reset your password.
                  </p>

                  <form onSubmit={forgotForm.handleSubmit(handleForgotSubmit)} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="email"
                          {...forgotForm.register('email', {
                            required: 'Email is required',
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                          })}
                          className={`input-field pl-10 ${forgotForm.formState.errors.email ? 'border-red-300 focus:ring-red-400' : ''}`}
                          placeholder="you@email.co.uk"
                        />
                      </div>
                      {forgotForm.formState.errors.email && (
                        <p className="mt-1.5 text-xs text-red-600">{forgotForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn-primary w-full py-3 text-base mt-2"
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Sending reset link...
                        </span>
                      ) : (
                        <>Send Reset Link <ArrowRight size={16} /></>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}

          {/* Signup Form */}
          {mode === 'signup' && (
            <div className="fade-in">
              <h2 className="text-2xl font-bold text-slate-900 mb-1">Start fighting back</h2>
              <p className="text-sm text-slate-500 mb-7">Create your free Disputely account</p>

              <button className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all duration-150 mb-5">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Sign up with Google
              </button>

              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">or create with email</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Full name</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      {...signupForm.register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
                      className={`input-field pl-10 ${signupForm.formState.errors.fullName ? 'border-red-300 focus:ring-red-400' : ''}`}
                      placeholder="Your full name"
                    />
                  </div>
                  {signupForm.formState.errors.fullName && (
                    <p className="mt-1.5 text-xs text-red-600">{signupForm.formState.errors.fullName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      {...signupForm.register('email', {
                        required: 'Email is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                      })}
                      className={`input-field pl-10 ${signupForm.formState.errors.email ? 'border-red-300 focus:ring-red-400' : ''}`}
                      placeholder="you@email.co.uk"
                    />
                  </div>
                  {signupForm.formState.errors.email && (
                    <p className="mt-1.5 text-xs text-red-600">{signupForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                  <p className="text-xs text-slate-400 mb-1.5">Minimum 8 characters, including one uppercase and one number</p>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...signupForm.register('password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Password must be at least 8 characters' },
                        pattern: { value: /^(?=.*[A-Z])(?=.*\d)/, message: 'Must include at least one uppercase letter and one number' },
                      })}
                      className={`input-field pl-10 pr-10 ${signupForm.formState.errors.password ? 'border-red-300 focus:ring-red-400' : ''}`}
                      placeholder="Create a strong password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.password && (
                    <p className="mt-1.5 text-xs text-red-600">{signupForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                      className={`input-field pl-10 pr-10 ${signupForm.formState.errors.confirmPassword ? 'border-red-300 focus:ring-red-400' : ''}`}
                      placeholder="Repeat your password"
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.confirmPassword && (
                    <p className="mt-1.5 text-xs text-red-600">{signupForm.formState.errors.confirmPassword.message}</p>
                  )}
                </div>

                <div className="space-y-2.5 pt-1">
                  <div className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      id="termsAccepted"
                      {...signupForm.register('termsAccepted', { required: 'You must accept the terms to continue' })}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 mt-0.5"
                    />
                    <label htmlFor="termsAccepted" className="text-sm text-slate-600 leading-relaxed">
                      I agree to the{' '}
                      <Link href="#" className="text-blue-600 hover:underline font-medium">Terms of Service</Link>
                      {' '}and{' '}
                      <Link href="#" className="text-blue-600 hover:underline font-medium">Privacy Policy</Link>
                    </label>
                  </div>
                  {signupForm.formState.errors.termsAccepted && (
                    <p className="text-xs text-red-600">{signupForm.formState.errors.termsAccepted.message}</p>
                  )}

                  <div className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      id="gdprConsent"
                      {...signupForm.register('gdprConsent', { required: 'GDPR consent is required under UK data law' })}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 mt-0.5"
                    />
                    <label htmlFor="gdprConsent" className="text-sm text-slate-600 leading-relaxed">
                      I consent to Disputely processing my personal data to provide dispute assistance, in accordance with the UK GDPR and Data Protection Act 2018.
                    </label>
                  </div>
                  {signupForm.formState.errors.gdprConsent && (
                    <p className="text-xs text-red-600">{signupForm.formState.errors.gdprConsent.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3 text-base mt-2"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Creating account...
                    </span>
                  ) : (
                    <>Create Free Account <ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              <p className="mt-5 text-center text-sm text-slate-500">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Sign in
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}